import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { ObjectStorageService } from '@app/object-storage/core/services/object-storage.service';
import { AttachDocument } from '@app/order/models/order-line-commands.model';
import { AttachDocumentType } from '@app/rfq/private/components/attach-document-dialog/interfaces/enums/attach-document-type.enum';
import { HotToastService } from '@ngneat/hot-toast';
import { Content } from '@ngneat/overview';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { BehaviorSubject, Subject, debounceTime, take, takeUntil, tap, zip } from 'rxjs';

@Component({
  selector: 'tc-file-drop-zone',
  templateUrl: './file-drop-zone.component.html',
  styleUrls: ['./file-drop-zone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileDropZoneComponent implements OnDestroy {
  @Input()
  actingAsCompanyId?: string;
  @Input() types: string[] = [
    AttachDocumentType.GENERAL,
    AttachDocumentType.PROFORMA_INVOICE,
    AttachDocumentType.CERTIFICATE,
    AttachDocumentType.INVOICE,
    AttachDocumentType.PROOF_DELIVERY,
    AttachDocumentType.PACKAGING_INSTRUCTION,
    AttachDocumentType.PROOF_OF_PAYMENT,
  ];

  @ViewChild('template') template: Content | undefined;

  @Output()
  readonly attachDocument = new EventEmitter<AttachDocument>();

  public readonly isDragging$ = new BehaviorSubject<boolean>(false);
  public readonly isUploading$ = new BehaviorSubject<boolean>(false);
  private dragLeaveSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private toast: HotToastService,
    private objectStorageService: ObjectStorageService,
    private elementRef: ElementRef,
    private viewportScroller: ViewportScroller,
  ) {
    this.setupDragLeaveDebounce();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDragLeaveDebounce(): void {
    this.dragLeaveSubject.pipe(
      debounceTime(10000),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.isDragging$.next(false);
    });
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.viewportScroller.scrollToPosition([0, 0]);
    this.isDragging$.next(true);
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragLeaveSubject.next();
  }

  public onFileDropped(files: NgxFileDropEntry[], type: AttachDocumentType): void {
    this.isDragging$.next(false);
    let counter = files.length;
    this.isUploading$.next(true);

    for (const newfile of files) {
      if (newfile.fileEntry.isFile) {
        const fileEntry = newfile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          const toast$ = this.toast.loading(this.template,
            {
              data: {
                name: file.name,
              },
              theme: 'snackbar',
              duration: 5000,
              position: 'bottom-center',
              style: {
                fontFamily: 'sans-serif',
              },
              className: 'hot-toast-custom-class',
            },
          ).afterClosed;

          const docUploaded$ = this.objectStorageService.documentUpload$(file);

          const combined$ = zip(toast$, docUploaded$);

          combined$.pipe(
            tap(([toast, doc]) => {
              if (!toast.dismissedByAction) {
                this.attachDocument.emit({
                  addedByCompanyId: this.actingAsCompanyId,
                  documents: [
                    {
                      objectId: doc.id,
                      name: file.name,
                      type: type as AttachDocumentType,
                    }
                  ]
                });
              }
              counter--;

              if (counter === 0) {
                this.isUploading$.next(false);
              }
            }),
            take(1),
            takeUntil(this.destroy$),
          ).subscribe();
        });
      }
    }
  }

}
